// src/pages/Auth/ForgotPassword.jsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Slide,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Business,
  Email,
  VpnKey,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Security,
  Shield,
  Speed,
  Support,
  Visibility,
  VisibilityOff,
  Send,
  AccessTime,
  Lock,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Services
import AuthService from '../../services/authService';

// Brand Config
import { BRAND_CONFIG } from '../../config/brandConfig';

// Validation schemas for different steps
const getValidationSchema = (step) => {
  const schemas = {
    0: yup.object({
      email: yup
        .string()
        .required('Email address is required')
        .email('Please enter a valid email address')
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          'Invalid email format'
        ),
    }),
    1: yup.object({
      verificationCode: yup
        .string()
        .required('Verification code is required')
        .length(6, 'Verification code must be 6 digits')
        .matches(/^[0-9]{6}$/, 'Please enter only numbers'),
    }),
    2: yup.object({
      newPassword: yup
        .string()
        .required('New password is required')
        .min(8, 'Must be at least 8 characters')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          'Must contain at least one uppercase letter, lowercase letter, and number'
        ),
      confirmPassword: yup
        .string()
        .required('Password confirmation is required')
        .oneOf([yup.ref('newPassword')], 'Passwords do not match'),
    }),
  };
  return schemas[step];
};

const steps = [
  { label: 'Email', icon: <Email /> },
  { label: 'Code Verification', icon: <VpnKey /> },
  { label: 'New Password', icon: <Security /> }
];

const features = [
  {
    title: 'Secure Verification',
    description: 'Identity verification with code sent to your email address',
    icon: <Shield />,
  },
  {
    title: 'Fast Process',
    description: 'Reset your password in minutes',
    icon: <Speed />,
  },
  {
    title: '24/7 Support',
    description: 'Our support team is with you when you experience problems',
    icon: <Support />,
  },
];

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const { control, handleSubmit, formState: { errors, isValid }, watch, reset } = useForm({
    resolver: yupResolver(getValidationSchema(activeStep)),
    mode: 'onChange',
    defaultValues: {
      email: '',
      verificationCode: '',
      newPassword: '',
      confirmPassword: '',
    }
  });

  const watchedValues = watch();

  // Timer for resend code
  React.useEffect(() => {
    let interval;
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            setCanResend(true);
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timeLeft]);

  const handleNext = async (data) => {
    setLoading(true);
    
    try {
      if (activeStep === 0) {
        // Send verification code
        await new Promise(resolve => setTimeout(resolve, 2000));
        setEmail(data.email);
        setActiveStep(1);
        setTimeLeft(300); // 5 minutes
        setCanResend(false);
        
        window.showToast && window.showToast(
          'Verification code sent to your email address',
          'success'
        );
      } else if (activeStep === 1) {
        // Verify code
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Simulate code verification
        if (data.verificationCode === '123456') {
          setActiveStep(2);
          window.showToast && window.showToast(
            'Code verified. You can now set your new password',
            'success'
          );
        } else {
          throw new Error('Verification code is incorrect');
        }
      } else if (activeStep === 2) {
        // Reset password
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        window.showToast && window.showToast(
          'Your password has been successfully updated',
          'success'
        );
        
        navigate('/login', {
          state: {
            message: 'Your password has been successfully updated. You can now sign in with your new password.'
          }
        });
      }
    } catch (error) {
      window.showToast && window.showToast(
        error.message || 'An error occurred. Please try again.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setTimeLeft(300);
      setCanResend(false);
      
      window.showToast && window.showToast(
        'New verification code sent',
        'success'
      );
    } catch (error) {
      window.showToast && window.showToast(
        'Code could not be sent. Please try again.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      navigate('/login');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Enter the email address where we will send the password reset link
              </Typography>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Your Registered Email Address"
                    type="email"
                    placeholder="example@yourcompany.com"
                    error={!!errors.email}
                    helperText={errors.email?.message || ''}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color={field.value?.includes('@') ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  A 6-digit verification code will be sent to your email address. 
                  This code will be valid for 5 minutes.
                </Typography>
              </Alert>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Slide direction="left" in={true} timeout={500}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Enter the 6-digit code sent to <strong>{email}</strong>
              </Typography>

              <Controller
                name="verificationCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Verification Code"
                    placeholder="123456"
                    error={!!errors.verificationCode}
                    helperText={errors.verificationCode?.message || ''}
                    sx={{ mb: 3 }}
                    inputProps={{ 
                      maxLength: 6,
                      style: { 
                        textAlign: 'center', 
                        fontSize: '1.5rem', 
                        letterSpacing: '0.5rem' 
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKey color={field.value?.length === 6 ? 'success' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime color="action" />
                      <Typography variant="body2">
                        Time remaining: <strong>{formatTime(timeLeft)}</strong>
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleResendCode}
                      disabled={!canResend || loading}
                      startIcon={<Send />}
                    >
                      {canResend ? 'Resend' : `${formatTime(timeLeft)}`}
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Didn't receive the code? Check your spam folder or 
                  {timeLeft === 0 ? ' request a new code.' : ` try again after ${formatTime(timeLeft)}.`}
                </Typography>
              </Alert>
            </Box>
          </Slide>
        );

      case 2:
        return (
          <Fade in={true} timeout={500}>
            <Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                Set Your New Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Create a secure and strong password
              </Typography>

              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message || 'At least 8 characters, uppercase, lowercase and number'}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color={field.value ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            sx={{ minWidth: 'auto', p: 1 }}
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message || ''}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color={field.value ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            sx={{ minWidth: 'auto', p: 1 }}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Security Tip:</strong> Do not use your password on any other platform 
                  and update it regularly.
                </Typography>
              </Alert>
            </Box>
          </Fade>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          
          {/* Left Side - Branding & Features */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' }, color: 'white' }}>
            {/* Logo & Branding */}
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img 
                    src={BRAND_CONFIG.logo} 
                    alt={BRAND_CONFIG.name}
                    style={{ 
                      width: '56px', 
                      height: '56px', 
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {BRAND_CONFIG.name}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
                    {BRAND_CONFIG.tagline}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
                Secure Password Reset
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                Regain secure access to your account. You can easily reset your password 
                with our advanced security protocols.
              </Typography>
            </Box>

            {/* Features */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {features.map((feature, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.5 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Side - Reset Form */}
          <Box sx={{ flex: 1, maxWidth: 500 }}>
            <Paper elevation={24} sx={{ p: 5, borderRadius: 4 }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Forgot Password
                </Typography>
              </Box>

              {/* Stepper */}
              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel 
                        icon={
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: activeStep >= index ? 'primary.main' : 'grey.300',
                            color: 'white',
                            fontSize: '0.875rem'
                          }}>
                            {activeStep > index ? <CheckCircle /> : step.icon}
                          </Box>
                        }
                      >
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {/* Form Content */}
              <Box component="form" onSubmit={handleSubmit(handleNext)}>
                {getStepContent(activeStep)}

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
                  <Button
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                  >
                    {activeStep === 0 ? 'Sign In' : 'Back'}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !isValid}
                    endIcon={activeStep === 2 ? <CheckCircle /> : <ArrowForward />}
                    sx={{ 
                      minWidth: 140,
                      py: 1.5,
                      background: activeStep === 2 
                        ? 'linear-gradient(135deg, #00c853 0%, #00a726 100%)'
                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      '&:hover': {
                        background: activeStep === 2
                          ? 'linear-gradient(135deg, #00a726 0%, #007c1f 100%)'
                          : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      }
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} color="inherit" />
                        Processing...
                      </Box>
                    ) : activeStep === 2 ? (
                      'Update Password'
                    ) : activeStep === 1 ? (
                      'Verify Code'
                    ) : (
                      'Send Code'
                    )}
                  </Button>
                </Box>
              </Box>

              {/* Footer */}
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Help
                </Typography>
              </Divider>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1}}>
                <Typography variant="body2" color="text.secondary">
                  Having trouble?
                </Typography>
                <Button 
                  variant="text"
                  onClick={() => window.open('mailto:reyhan@qualessolutions.com')}
                  sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                >
                  Contact Support Team
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;